// Tenancy — the deployment serves TWO hostnames off ONE process, ONE build and
// ONE database:
//
//   saudi-tourism-demand-sensing-agent.canvasx.ai → tenant "saudi"      (the client)
//   wonderland-demand-sensing-agent.canvasx.ai    → tenant "wonderland" (the demo)
//
// The pair the deployment launched on still resolve the same way and are still
// served, so links already shared keep working:
//
//   ow-traveller-segmentation.canvasx.ai          → tenant "saudi"
//   ow-traveller-segmentation-demo.canvasx.ai     → tenant "wonderland"
//
// The two differ only in their country of focus. Global sections (UNWTO world
// arrivals, corridors, forecasts, affordability) are the SAME real data on both
// — only the country being analysed changes, and with it every province, gateway,
// currency and copy string that names it.
//
// This module is the single source of truth for both halves of that:
//
//   • WHICH tenant a request belongs to — resolved from the Host header on the
//     server and from window.location.hostname in the browser, so the same rule
//     runs on both sides (see tenantFromHost).
//   • WHAT that tenant is — the TenantProfile, which every component reads
//     instead of hardcoding "Saudi" / "SAR" / the 13 provinces.
//
// Row-level scoping (the `tenant` column on the saudi_*/gtm_* tables) is a
// separate mechanism layered on top; this module names the tenants it uses.

// --- Identity -------------------------------------------------------------

export const TENANT_IDS = ["saudi", "wonderland"] as const;
export type TenantId = (typeof TENANT_IDS)[number];

/** The client deployment. Every pre-existing DB row belongs to this tenant, so
 *  it is also the `DEFAULT` of the `tenant` column and the fallback whenever a
 *  request carries no recognisable host (tests, curl, the in-process warmer). */
export const DEFAULT_TENANT: TenantId = "saudi";

export function isTenantId(value: unknown): value is TenantId {
  return typeof value === "string" && (TENANT_IDS as readonly string[]).includes(value);
}

// --- Host → tenant --------------------------------------------------------

/** Hostnames that serve the demo. Matched exactly, after lowercasing and
 *  stripping any port. Add a host here to point it at Wonderland. */
const WONDERLAND_HOSTS: readonly string[] = [
  "wonderland-demand-sensing-agent.canvasx.ai",
  // The hostname the demo launched on. Kept because links to it are already out.
  "ow-traveller-segmentation-demo.canvasx.ai",
];

/**
 * The demo tenant's former id, still accepted everywhere the new one is.
 *
 * The demo was called `narnia` until the client asked for Wonderland. An
 * override is not cosmetic: `?tenant=narnia` on a bookmarked link, or a
 * `narnia.localhost` dev host, would otherwise fall through the unrecognised
 * branch to DEFAULT_TENANT and quietly serve THE CLIENT'S data under a link
 * someone shared as the demo. Mapping it forward is the safe failure.
 *
 * Safe to delete once no such link can exist — nothing writes it.
 */
const LEGACY_WONDERLAND_ALIASES: readonly string[] = ["narnia"];

/**
 * Resolve a tenant from a Host header or `window.location.hostname`.
 *
 * Anything unrecognised resolves to `saudi` — the production hostname, every
 * dev hostname, `localhost`, and an absent header all keep serving the client
 * deployment. Wonderland is opt-in by host, never by accident.
 *
 * For local work against the demo without DNS, any host whose first label
 * starts with `wonderland` also resolves (e.g. `wonderland.localhost:5173`).
 */
export function tenantFromHost(host: string | null | undefined): TenantId {
  if (!host) return DEFAULT_TENANT;
  // Strip the port. Bracketed IPv6 literals ("[::1]:5173") keep their colons
  // inside the brackets, so only split on a colon after the closing bracket.
  const bare =
    host
      .trim()
      .toLowerCase()
      .replace(/^\[([^\]]+)\].*$/, "$1")
      .split(":")[0] ?? "";
  if (!bare) return DEFAULT_TENANT;
  if (WONDERLAND_HOSTS.includes(bare)) return "wonderland";
  const label = bare.split(".")[0] ?? "";
  if (label.startsWith("wonderland")) return "wonderland";
  if (LEGACY_WONDERLAND_ALIASES.some((alias) => label.startsWith(alias))) return "wonderland";
  return DEFAULT_TENANT;
}

/**
 * Resolve the tenant for a request, honouring an explicit override.
 *
 * The override (an `x-ow-tenant` header or a `?tenant=` query param) exists so
 * the demo is testable before its DNS record and TLS cert are in place, and so
 * one browser can hold both deployments open. It is deliberately NOT a security
 * boundary: the two tenants differ only in which country's numbers they show,
 * both sit behind the same login, and Wonderland's data is fabricated. An
 * unrecognised override value is ignored rather than rejected.
 */
export function resolveTenant(input: { host?: string | null; override?: string | null }): TenantId {
  if (isTenantId(input.override)) return input.override;
  const override = input.override?.trim().toLowerCase();
  if (override && LEGACY_WONDERLAND_ALIASES.includes(override)) return "wonderland";
  return tenantFromHost(input.host);
}

/** Header carrying an explicit tenant override. The browser sends it on every
 *  dashboard read so an override survives into the API call; the cache varies
 *  on it so a cached body can never cross tenants. */
export const TENANT_HEADER = "x-ow-tenant";

// --- Profile --------------------------------------------------------------

export interface TenantRegion {
  /** Stable code used as the DB `region_id` and the province-tile key. */
  id: string;
  name: string;
  /** Abbreviated label for the province tiles, which are ~90px wide. */
  short: string;
}

export interface TenantGateway {
  /** IATA code. Verified free against `aviation_airports` for Wonderland so its
   *  fabricated flight rows can never mix with real ones. */
  code: string;
  name: string;
  lat: number;
  lon: number;
}

export interface TenantProfile {
  id: TenantId;
  /** Country name as it appears in prose and headings: "Saudi Arabia". */
  name: string;
  /** The short form the UI actually uses in most labels: "Saudi", "Wonderland". */
  shortName: string;
  /** Adjective form: "Saudi", "Wonderland". */
  adjective: string;
  /** The stand-in the copy uses for variety: "the Kingdom". The demo has no
   *  such stand-in — the client asked for every "the Realm" to read
   *  "Wonderland" — so on that tenant this is simply the country's name and the
   *  copy sites that interpolate it stay unchanged. */
  theCountry: string;
  /** ISO3 used as the country key across the data layer. `WON` is not an
   *  ISO-assigned code — it is unassigned, chosen for readability, and verified
   *  free against every iso3-keyed table before the rename off `NAR`. */
  iso3: string;
  /** ISO2, used where a two-letter geo is needed. Wonderland uses `XW` from the
   *  user-assigned range: the obvious `WO` is unassigned but not reserved for
   *  private use, and `WL` is likewise not ours to take. */
  iso2: string;
  capital: string;
  currency: { code: string; label: string };
  /**
   * The national tourism plan the 2030 targets belong to, and its horizon.
   *
   * Both tenants target 2030, so every 2030-target and 2030-opportunity
   * calculation in the repos stays unchanged across tenants — only the plan's
   * NAME differs. "Vision 2030" is the client's own programme and naming it on
   * the demo hands the reader the client's identity in one string, which is why
   * this is a profile field and never a literal in a component.
   */
  visionPlan: { label: string; targetYear: number };
  /** The national tourism body the dashboard is built for, and the acronym the
   *  shell wordmark uses. "STA" was a literal in the login hero, the landing
   *  hub and the shell footer, so the demo signed itself as the client. */
  tourismAuthority: { name: string; short: string };

  /**
   * How the focus country's OWN documents are cited in a published source line.
   *
   * These are the labels that gave the demo away most loudly, because a source
   * line is the one place a section is *supposed* to name an institution: the
   * client's screens read "Source · TQ MT Demand Model", "Saudi MoT TIC open
   * data", "TQ Air connectivity workbook", and those strings were literals in
   * the components, so the demo published the client's ministry under a
   * fabricated country's numbers.
   *
   * Third-party sources (Euromonitor, UNWTO, Aviation Edge, Google Trends,
   * Passport Index) are NOT here and must never be renamed per tenant — they are
   * real vendors and the same vendor on both deployments. Only the labels naming
   * THIS country's own government belong in this block.
   */
  sources: {
    /** The ministry's inbound demand + targets workbook. */
    demandModel: string;
    /** The ministry's open tourism statistics (establishment counts, etc.). */
    tourismStats: string;
    /** The seat-capacity workbook behind the connectivity sections. */
    connectivityWorkbook: string;
    /** The ministry itself, cited in prose. */
    ministry: string;
    /** The ministry's own acronym, for labels that name its targets ("MT target share"). */
    ministryShort: string;
  };
  /** Host of the official entry-requirements portal, cited under the visa map. */
  visaAuthority: string;
  /** The national carrier, named in the connectivity copy. */
  flagCarrier: string;

  /**
   * Does this country have a religious travel segment the analysis models?
   *
   * Saudi's inbound is religious-led — Umrah and Hajj are 41.5% of its purpose
   * mix — so the prioritization engine nets religious arrivals off before
   * sizing opportunity, the supply cuts hold a religious category out of every
   * "leisure" figure, and the copy says "non-religious" because that is what
   * the number is.
   *
   * Almost nowhere else is that true, which is why the client asked for it off
   * the demo entirely. Where this is false: no religious strip is seeded, no
   * religious purpose or supply category exists, and the copy drops the
   * qualifier rather than netting off a zero and still calling the result
   * "non-religious arrivals" — a distinction the reader cannot check, on a
   * subject the deployment has nothing to say about.
   *
   * The engine's arithmetic is unchanged either way: with no strip to subtract,
   * current non-religious arrivals ARE current arrivals.
   */
  religiousSegment: boolean;
  modelsEveryTargetedMarket: boolean;
  /** Retransform Estimator A's log-share fits to levels (Duan smearing) instead of publishing the geometric-mean exp(Xβ). */
  retransformLogShare: boolean;
  /** Whether the deployment offers the Ask assistant at all: the launcher, the
   *  mobile Ask tab and the chat routes. Off on the demo. */
  assistantEnabled: boolean;
  /**
   * The gravity model's two destination drivers for the focus country when the
   * reference tables carry no row for it: the WEF TTDI T&T Resources subindex
   * (1–7) and the UN Visa Openness Index score (0–100). The client's country is
   * in both tables and sets neither; the demo's invented country is in neither.
   */
  modelInputs?: { offering: number; openness: number };
  /**
   * The marquee leisure destination — the one a demo screen names when it needs
   * a concrete example of a trip into this country. "ALULA" was a literal in
   * the landing hub's simulator label, so the demo advertised a Saudi
   * destination on its front page. Not a province: AlUla is a place inside
   * Madinah, and the point of the field is that it is the recognisable name.
   */
  heroDestination: string;
  /**
   * The country's UTC offset, in hours. Both of these countries observe one
   * offset year-round with no DST, so a single number is exact rather than a
   * summer approximation. Read by the connectivity timing panel, which turns a
   * departure time somewhere else into the local time on the ground here.
   */
  utcOffset: number;
  /**
   * Source markets whose inflow is dominated by land or causeway crossings.
   * The prioritization matrix excludes them by default; an island has none.
   */
  landBorderNeighbours: readonly string[];
  regions: readonly TenantRegion[];
  gateways: readonly TenantGateway[];
}

const SAUDI: TenantProfile = {
  id: "saudi",
  name: "Saudi Arabia",
  shortName: "Saudi",
  adjective: "Saudi",
  theCountry: "the Kingdom",
  iso3: "SAU",
  iso2: "SA",
  capital: "Riyadh",
  currency: { code: "SAR", label: "Saudi riyal" },
  visionPlan: { label: "Vision 2030", targetYear: 2030 },
  tourismAuthority: { name: "Saudi Tourism Authority", short: "STA" },
  sources: {
    demandModel: "TQ MT Demand Model",
    tourismStats: "Saudi MoT TIC open data",
    connectivityWorkbook: "TQ Air connectivity workbook",
    ministry: "Saudi Ministry of Tourism",
    ministryShort: "MT",
  },
  visaAuthority: "visa.visitsaudi.com",
  flagCarrier: "Saudia",
  religiousSegment: true,
  modelsEveryTargetedMarket: false,
  retransformLogShare: false,
  assistantEnabled: true,
  heroDestination: "AlUla",
  utcOffset: 3,
  landBorderNeighbours: ["BHR", "KWT", "QAT", "ARE", "OMN"],
  // The 13 administrative provinces, in the order the tile grid lays them out
  // (roughly north → south). Lifted verbatim from SA_REGIONS in
  // saudi-intel-sections.tsx, which Phase 4 deletes in favour of this.
  regions: [
    { id: "NB", name: "Northern Borders", short: "N. Borders" },
    { id: "JWF", name: "Al Jawf", short: "Al Jawf" },
    { id: "TAB", name: "Tabuk", short: "Tabuk" },
    { id: "HAL", name: "Ha’il", short: "Ha’il" },
    { id: "QAS", name: "Qassim", short: "Qassim" },
    { id: "EP", name: "Eastern Province", short: "Eastern" },
    { id: "MAD", name: "Madinah", short: "Madinah" },
    { id: "RUH", name: "Riyadh", short: "Riyadh" },
    { id: "MAK", name: "Makkah", short: "Makkah" },
    { id: "BAH", name: "Al Bahah", short: "Al Bahah" },
    { id: "ASR", name: "Asir", short: "Asir" },
    { id: "NAJ", name: "Najran", short: "Najran" },
    { id: "JAZ", name: "Jazan", short: "Jazan" },
  ],
  // Lifted from SAUDI_GATEWAYS in gtm-route-motion.tsx.
  gateways: [
    { code: "JED", name: "Jeddah", lat: 21.68, lon: 39.16 },
    { code: "RUH", name: "Riyadh", lat: 24.96, lon: 46.7 },
    { code: "DMM", name: "Dammam", lat: 26.47, lon: 49.8 },
    { code: "MED", name: "Medina", lat: 24.55, lon: 39.7 },
    { code: "AHB", name: "Abha", lat: 18.24, lon: 42.66 },
    { code: "TUU", name: "Tabuk", lat: 28.37, lon: 36.62 },
  ],
};

// Wonderland occupies an invented landmass in the empty central Indian Ocean —
// roughly lon 66°–79°E, lat 12°–24°S. Nothing real sits in that box (Mauritius
// and Réunion are west of 58°E, the Chagos archipelago north of 7°S, Cocos and
// Christmas Island east of 96°E), so its polygon can be dropped into the world
// basemap without overlapping a real country, and it is tropical and roughly
// equidistant from Europe, Asia and Australia — which keeps its inbound mix and
// route geography plausible. The polygon itself lands in Phase 2; every gateway
// below sits inside that box.
//
// The PROVINCES read as an ex-colonial Indian Ocean island: French-creole names
// on the coast (Port Célestin, Baie Claire, Anse Verdier, Pointe Marcelin) and
// English ones inland, which is the two-layer pattern Mauritius, the Seychelles
// and Réunion actually show. They used to be the books' names — Glasswater,
// Lantern Waste, Cair Paravel — which read as fiction on a client screen and
// undercut a demo whose whole job is to look like the real product.
//
// The province IDS and the gateway IATA CODES are deliberately unchanged. The
// ids are the join key for every seeded row, every map feature and every test;
// the six IATA codes were verified free against all 11,741 rows of
// `aviation_airports` before they were chosen, and re-picking them to match the
// new names would have to clear that check again for no gain. A code that does
// not spell its city is what real airports look like anyway (ORD, YYZ, LHR).
const WONDERLAND: TenantProfile = {
  id: "wonderland",
  name: "Wonderland",
  shortName: "Wonderland",
  adjective: "Wonderland",
  theCountry: "Wonderland",
  iso3: "WON",
  iso2: "XW",
  capital: "Port Célestin",
  currency: { code: "WLC", label: "Wonderland crown" },
  visionPlan: { label: "Wonderland Tourism Strategy 2030", targetYear: 2030 },
  tourismAuthority: { name: "Wonderland Tourism Authority", short: "WTA" },
  sources: {
    demandModel: "WTA Demand Model",
    tourismStats: "Wonderland Ministry of Tourism open data",
    connectivityWorkbook: "WTA air connectivity workbook",
    ministry: "Wonderland Ministry of Tourism",
    ministryShort: "WMT",
  },
  visaAuthority: "visa.visitwonderland.com",
  flagCarrier: "Wonderland Air",
  religiousSegment: false,
  modelsEveryTargetedMarket: true,
  retransformLogShare: true,
  assistantEnabled: false,
  modelInputs: { offering: 5.0, openness: 25 },
  // The seeded supply catalogue's marquee leisure coast (seed/wonderland/supply.ts).
  heroDestination: "Baie Claire",
  // +5, from the landmass's own longitude (66°–79°E). No DST.
  utcOffset: 5,
  landBorderNeighbours: [],
  regions: [
    { id: "LNW", name: "Northfield", short: "Northfield" },
    { id: "ETM", name: "Kingsmoor", short: "Kingsmoor" },
    { id: "WWD", name: "Westbourne", short: "Westbourne" },
    { id: "OWL", name: "Ashgrove", short: "Ashgrove" },
    { id: "BVD", name: "Millbrook", short: "Millbrook" },
    { id: "GRV", name: "Grand River", short: "Grand River" },
    { id: "BRU", name: "Anse Verdier", short: "Anse Verdier" },
    { id: "CPV", name: "Port Célestin", short: "Célestin" },
    { id: "GLW", name: "Baie Claire", short: "Baie Claire" },
    { id: "ANV", name: "Pointe Marcelin", short: "Marcelin" },
    { id: "ARM", name: "Elmsford", short: "Elmsford" },
    { id: "TLM", name: "Clarendon", short: "Clarendon" },
  ],
  gateways: [
    { code: "CVP", name: "Port Célestin", lat: -18.4, lon: 77.2 },
    { code: "BEA", name: "Anse Verdier", lat: -17.1, lon: 72.6 },
    { code: "LNT", name: "Northfield", lat: -13.8, lon: 68.4 },
    { code: "GSW", name: "Baie Claire", lat: -22.3, lon: 76.1 },
    { code: "AVD", name: "Pointe Marcelin", lat: -22.9, lon: 71.2 },
    { code: "EMR", name: "Kingsmoor", lat: -13.2, lon: 74.0 },
  ],
};

export const TENANTS: Record<TenantId, TenantProfile> = { saudi: SAUDI, wonderland: WONDERLAND };

export function tenantProfile(id: TenantId): TenantProfile {
  return TENANTS[id];
}

/**
 * ISO3 codes that do not exist in the real world — the demo tenants' focus
 * countries.
 *
 * Some tables are shared across tenants rather than tenant-columned because a
 * row is already keyed by the country it describes: the Euromonitor bilateral
 * panel holds every destination on earth, and Wonderland is simply one more
 * `dest_iso3`. That works for the reads that name a focus country, which are
 * scoped by `focusIso3(tenant)` and so can only ever see their own.
 *
 * It does NOT work for the reads that roll the table up across ALL countries —
 * global arrivals, the corridor chord, the world trend, the gravity model's
 * outbound totals. Those must stay real and identical on both hostnames (that
 * is an explicit product decision), so a fabricated country entering one of
 * them would be visible on the CLIENT's dashboard. At Wonderland's seeded scale it
 * would rank inside the published Top-10 growth board.
 *
 * Hence: every cross-country read filters these out. See the server's
 * `notFabricated()` helper for the query-side predicate.
 */
export const FABRICATED_ISO3S: readonly string[] = TENANT_IDS.filter((id) => id !== DEFAULT_TENANT).map(
  (id) => TENANTS[id].iso3,
);
